import { useCallback, useEffect, useState } from 'react'
import type { UnsplashPhoto } from '@/lib/api/features/unsplash'
import {
  confirmUpload,
  getDownloadUrl,
  getPresignedUploadUrl,
} from '@/lib/api/features/attachments'
import { trackDownload } from '@/lib/api/features/unsplash'
import { getUploadContentType } from '@/lib/utils/fileMime'

export interface CoverImageMeta {
  positionY: number
  unsplashAttribution?: {
    authorName: string
    authorUrl: string
    photoUrl: string
  }
}

interface UseCoverImageProps {
  entityId: string
  workspaceId: string
  coverImageUrl: string | null | undefined
  coverImageType: string | null | undefined
  coverImageMeta: string | null | undefined
  updateEntity: (id: string, data: Record<string, unknown>) => Promise<void>
  uploadCategory: string
}

export const useCoverImage = ({
  entityId,
  workspaceId,
  coverImageUrl,
  coverImageType,
  coverImageMeta,
  updateEntity,
  uploadCategory,
}: UseCoverImageProps) => {
  const [displayUrl, setDisplayUrl] = useState<string | null>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const meta: CoverImageMeta | null = coverImageMeta
    ? JSON.parse(coverImageMeta)
    : null

  // Resolve display URL for custom uploads (presigned download URL)
  useEffect(() => {
    if (!coverImageUrl) {
      setDisplayUrl(null)
      return
    }

    if (coverImageType === 'unsplash') {
      setDisplayUrl(coverImageUrl)
      return
    }

    // Custom upload - need to resolve presigned download URL
    // coverImageUrl stores the attachmentId for custom uploads
    let cancelled = false
    const resolve = async () => {
      try {
        const data = await getDownloadUrl(workspaceId, coverImageUrl, 3600)
        if (!cancelled) {
          setDisplayUrl(data.downloadUrl)
        }
      } catch {
        if (!cancelled) {
          setDisplayUrl(null)
        }
      }
    }
    void resolve()
    return () => {
      cancelled = true
    }
  }, [coverImageUrl, coverImageType, workspaceId])

  const setCoverFromUnsplash = useCallback(
    async (photo: UnsplashPhoto) => {
      const newMeta: CoverImageMeta = {
        positionY: 50,
        unsplashAttribution: {
          authorName: photo.user.name,
          authorUrl: photo.user.links.html,
          photoUrl: `https://unsplash.com/photos/${photo.id}`,
        },
      }

      await updateEntity(entityId, {
        coverImageUrl: photo.urls.regular,
        coverImageType: 'unsplash',
        coverImageMeta: JSON.stringify(newMeta),
      })

      // Track download per Unsplash API guidelines
      void trackDownload(photo.links.download_location)
    },
    [entityId, updateEntity],
  )

  const uploadCustomImage = useCallback(
    async (file: File) => {
      setIsUploading(true)
      setUploadProgress(0)

      try {
        const uploadContentType = getUploadContentType(file)

        // Step 1: Get presigned URL
        const presignedData = await getPresignedUploadUrl(
          workspaceId,
          uploadCategory,
          entityId,
          {
            fileName: file.name,
            fileType: uploadContentType,
            fileSizeBytes: file.size,
          },
        )

        setUploadProgress(10)

        // Step 2: Upload to R2
        const xhr = new XMLHttpRequest()

        await new Promise<void>((resolve, reject) => {
          xhr.upload.addEventListener('progress', (e) => {
            if (e.lengthComputable) {
              const progress = Math.round((e.loaded / e.total) * 80) + 10
              setUploadProgress(progress)
            }
          })

          xhr.addEventListener('load', () => {
            if (xhr.status >= 200 && xhr.status < 300) {
              resolve()
            } else {
              reject(new Error(`Upload failed with status ${xhr.status}`))
            }
          })

          xhr.addEventListener('error', () =>
            reject(
              new Error(
                xhr.status === 0
                  ? 'Network error uploading to R2. The browser could not read a response; check the private R2 bucket CORS settings for this web origin.'
                  : `Upload failed with status ${xhr.status}`,
              ),
            ),
          )
          xhr.addEventListener('abort', () => reject(new Error('Upload aborted')))

          xhr.open('PUT', presignedData.uploadUrl)
          xhr.setRequestHeader('Content-Type', uploadContentType)
          xhr.send(file)
        })

        setUploadProgress(90)

        // Step 3: Confirm upload
        await confirmUpload(workspaceId, presignedData.attachmentId)

        setUploadProgress(100)

        // Step 4: Update entity with the attachment ID
        const newMeta: CoverImageMeta = { positionY: 50 }
        await updateEntity(entityId, {
          coverImageUrl: presignedData.attachmentId,
          coverImageType: 'custom',
          coverImageMeta: JSON.stringify(newMeta),
        })
      } finally {
        setIsUploading(false)
        setUploadProgress(0)
      }
    },
    [entityId, workspaceId, updateEntity, uploadCategory],
  )

  const removeCoverImage = useCallback(async () => {
    await updateEntity(entityId, {
      coverImageUrl: null,
      coverImageType: null,
      coverImageMeta: null,
    })
  }, [entityId, updateEntity])

  const updatePosition = useCallback(
    async (positionY: number) => {
      const updatedMeta: CoverImageMeta = {
        ...(meta ?? { positionY: 50 }),
        positionY,
      }
      await updateEntity(entityId, {
        coverImageMeta: JSON.stringify(updatedMeta),
      })
    },
    [entityId, meta, updateEntity],
  )

  return {
    displayUrl,
    meta,
    hasCover: !!coverImageUrl,
    isUploading,
    uploadProgress,
    setCoverFromUnsplash,
    uploadCustomImage,
    removeCoverImage,
    updatePosition,
  }
}
